// Sugalarm — cycle 315: shared Alarm Reliability state for the Android
// mobile shell (the standard phone APK).
//
// One hook owns the plugin read + the "how many urgent-path protections
// still need setup" derivation, consumed by three surfaces: the Settings →
// Alarm Reliability screen, the Settings index row subtitle, and the Now
// screen's compact warn banner. It re-reads on app resume/foreground
// (visibilitychange + window focus — the cycle-226 CriticalAlertsCard
// pattern; the Capacitor webview dispatches visibilitychange on native
// resume) so statuses flip live after the user returns from a system
// settings screen, with no app restart.
//
// Off the Android standard shell (`isAvailable()` false — web, iOS, both
// kiosks, and any APK predating the plugin) everything is inert:
// `available` is false, `status` stays null, `unmetCount` is null.

import { useCallback, useEffect, useState } from "react";

import {
  getStatus,
  isAvailable,
  type AlarmReliabilityStatus,
} from "../lib/alarmReliability";

export interface ReliabilityItemState {
  key: "notifications" | "dnd" | "fullScreen" | "battery";
  granted: boolean;
  /** False → the row is hidden (e.g. full-screen below API 34). */
  applicable: boolean;
}

/** Derive the checklist from a raw status, tolerating the vc20 APK's
 *  missing cycle-315 fields (optimistic defaults — a stale APK must not
 *  scare the user with unfixable warnings). */
export function deriveReliabilityItems(
  s: AlarmReliabilityStatus,
): ReliabilityItemState[] {
  // Below API 34 the full-screen grant doesn't exist (always allowed) —
  // the row is hidden. An old APK omits sdkInt: treat the row as
  // applicable only when the grant is reported missing (actionable).
  const fullScreenApplicable =
    s.sdkInt != null ? s.sdkInt >= 34 : s.fullScreenIntent === false;
  return [
    {
      key: "notifications",
      granted: s.notificationsEnabled ?? true,
      applicable: true,
    },
    {
      key: "dnd",
      granted: s.dndAccess ?? s.dndPolicyAccess,
      applicable: true,
    },
    {
      key: "fullScreen",
      granted: s.fullScreenIntent,
      applicable: fullScreenApplicable,
    },
    {
      key: "battery",
      granted: s.batteryExempt ?? s.batteryOptimizationsIgnored,
      applicable: true,
    },
  ];
}

export function unmetCountOf(s: AlarmReliabilityStatus | null): number | null {
  if (s == null || !s.supported) return null;
  return deriveReliabilityItems(s).filter(
    (it) => it.applicable && !it.granted,
  ).length;
}

export interface UseAlarmReliability {
  /** True only inside the Android standard shell carrying the plugin. */
  available: boolean;
  status: AlarmReliabilityStatus | null;
  items: ReliabilityItemState[];
  /** Unmet applicable protections; null until the first read resolves. */
  unmetCount: number | null;
  refresh: () => void;
}

export function useAlarmReliability(): UseAlarmReliability {
  const available = isAvailable();
  const [status, setStatus] = useState<AlarmReliabilityStatus | null>(null);

  const refresh = useCallback(() => {
    if (!available) return;
    void getStatus().then((s) => {
      if (s != null) setStatus(s);
    });
  }, [available]);

  useEffect(() => {
    if (!available) return;
    refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", refresh);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refresh);
    };
  }, [available, refresh]);

  const items = status != null ? deriveReliabilityItems(status) : [];
  return {
    available,
    status,
    items,
    unmetCount: unmetCountOf(status),
    refresh,
  };
}
